/**
 * RoleCandidatesWorkspace.jsx — RC 1.62 Phase C.
 *
 * Internal sub-tab orchestrator for the Role Detail "Candidates" tab.
 * Splits the parent tab into two sub-tabs:
 *
 *   1. {Company} Imported Candidates — live MVP. Mounts
 *      RoleCandidatesPanel plus the sidebars (Reviewer coverage,
 *      Lifecycle timeline) that belong to the uploaded workflow.
 *   2. NDOR-Surfaced Candidates — Channel-2; gated. When the
 *      workspace has Channel-2 enabled, the live panel renders;
 *      otherwise the operator-only placeholder explains the
 *      entitlement gate ("Available for enabled workspaces").
 *
 * RC 1.62 Role Lifecycle Gate Hardening (2026-05-30):
 *   • Labels are now publisher-aware ("Acme Imported Candidates"
 *     / "NDOR-Surfaced Candidates") so the operator can see
 *     unambiguously which surface is company-supplied vs
 *     NDORFlow-surfaced.
 *   • Sub-tabs stay mounted for closing / closed roles so the
 *     operator can still triage decided candidates and assemble
 *     governance packs (only NEW uploads are blocked upstream by
 *     the parent tab).
 *
 * STRICT PHASE C BOUNDARY:
 *   • Composition slice only.
 *   • Surfaced sub-tab makes ZERO network calls when the
 *     placeholder renders.
 *   • Default sub-tab is "uploaded".
 *   • Sub-tab state is LOCAL — when the parent Candidates tab is
 *     deactivated, Radix unmounts this component, so a fresh visit
 *     (including the Phase-B upload auto-switch) always lands on
 *     Imported Candidates. That is the intended Phase C semantics.
 */
import { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

import { apiFetch } from '@/lib/api';
import RoleCandidatesPanel from './RoleCandidatesPanel';
import ReviewerCoveragePanel from './ReviewerCoveragePanel';
import LifecycleTimelinePanel from './LifecycleTimelinePanel';
import SurfacedCandidatesPlaceholder from './SurfacedCandidatesPlaceholder';
import RoleSurfacedCandidatesPanel from './RoleSurfacedCandidatesPanel';
import { useChannel2 } from '@/lib/useChannel2';

// Pull a short label out of the org name so tab triggers don't
// overflow on small viewports. "Acme Robotics" → "Acme".
// We keep the full name for accessible labelling.
function shortOrgLabel(name) {
  if (!name || typeof name !== 'string') return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  // First word — works well for "Acme Robotics", "Pivotal Health Ltd",
  // "Northshore Capital Partners". Honours user directive to use the
  // first word when layout-constrained.
  return trimmed.split(/\s+/)[0];
}

export default function RoleCandidatesWorkspace({
  role, refreshToken = 0,
  integrityFilterRequest = null, onIntegrityFilterApplied = null,
}) {
  const [sub, setSub] = useState('uploaded');
  const [orgName, setOrgName] = useState(null);
  // RC 1.62 Phase D — Channel-2 gate. Surfaced sub-tab renders the
  // live RoleSurfacedCandidatesPanel ONLY when the server projects
  // channel_2.enabled === true. Otherwise the placeholder stays.
  const channel2 = useChannel2();

  // Load the employer organisation name once for the publisher-aware
  // tab label. Fail-quietly: if the read fails we fall back to a
  // neutral "Imported Candidates" label.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await apiFetch('/api/employer/v1/me');
        const n = me?.organisation?.name || null;
        if (!cancelled) setOrgName(n);
      } catch {
        if (!cancelled) setOrgName(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const shortLabel    = shortOrgLabel(orgName);
  const uploadedLabel = shortLabel
    ? `${shortLabel} Imported Candidates`
    : 'Imported Candidates';
  const uploadedAccessible = orgName
    ? `${orgName} Imported Candidates`
    : 'Imported Candidates';

  return (
    <div data-testid="role-candidates-workspace">
      <Tabs value={sub} onValueChange={setSub} className="w-full">
        <TabsList
          className="bg-ndor-bg/40 border border-ndor-edge p-1 gap-1 h-auto"
          data-testid="candidates-subtabs"
        >
          <TabsTrigger
            value="uploaded"
            data-testid="candidates-subtab-uploaded"
            aria-label={uploadedAccessible}
            title={uploadedAccessible}
            className="text-[11px] sm:text-xs font-medium uppercase tracking-wider text-ndor-ink-secondary data-[state=active]:bg-ndor-surface data-[state=active]:text-ndor-ink data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_0_#C9A45C] hover:text-ndor-ink transition-colors"
          >
            {uploadedLabel}
          </TabsTrigger>
          <TabsTrigger
            value="surfaced"
            data-testid="candidates-subtab-surfaced"
            aria-label="NDOR-Surfaced Candidates"
            title="NDOR-Surfaced Candidates"
            className="text-[11px] sm:text-xs font-medium uppercase tracking-wider text-ndor-ink-secondary data-[state=active]:bg-ndor-surface data-[state=active]:text-ndor-ink data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_0_#C9A45C] hover:text-ndor-ink transition-colors"
          >
            NDOR-Surfaced Candidates
          </TabsTrigger>
        </TabsList>

        {/* ── Imported Candidates (Channel 1 — live MVP) ── */}
        <TabsContent
          value="uploaded"
          className="mt-4 focus-visible:outline-none"
          data-testid="uploaded-dossiers-panel"
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            <div className="space-y-6 lg:col-span-2 lg:space-y-8">
              <RoleCandidatesPanel
                roleId={role.id}
                roleTitle={role.title}
                role={role}
                refreshToken={refreshToken}
                integrityFilterRequest={integrityFilterRequest}
                onIntegrityFilterApplied={onIntegrityFilterApplied}
              />
            </div>
            <aside className="space-y-6">
              <ReviewerCoveragePanel roleId={role.id} />
              <LifecycleTimelinePanel roleId={role.id} />
            </aside>
          </div>
        </TabsContent>

        {/* ── NDOR-Surfaced Candidates ──
            Channel-2 gate: live `RoleSurfacedCandidatesPanel` when
            `channel_2.enabled === true`; placeholder otherwise.
            The placeholder is honest: this surface is BUILT but
            entitlement-gated ("Available for enabled workspaces"),
            not unfinished. ── */}
        <TabsContent
          value="surfaced"
          className="mt-4 focus-visible:outline-none"
          data-testid="surfaced-candidates-preview"
        >
          {channel2.loading ? (
            <div
              className="rounded border border-ndor-edge bg-ndor-surface-alt/30 px-5 py-6 text-sm text-ndor-ink-muted"
              data-testid="surfaced-candidates-loading"
            >
              Loading…
            </div>
          ) : channel2.enabled ? (
            <RoleSurfacedCandidatesPanel
              roleId={role.id}
              accountId={role.org_id || role.account_id || null}
              signalsRemaining={channel2.signals_remaining}
              signalsAllowance={channel2.signals_allowance}
              onSignalSent={channel2.refresh}
            />
          ) : (
            <SurfacedCandidatesPlaceholder roleId={role.id} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
